<script lang="ts" setup>
import { MdPreview } from 'md-editor-v3';
import 'md-editor-v3/lib/preview.css';
import type { SystemAnnouncementCategory } from '~/models/system-announcement';
import { SystemAnnouncementStatus } from '~/models/system-announcement';
import { formatDateTime } from '~/utils/datetime';
import { sanitizeMarkdownHtml } from '~/utils/sanitize-markdown-html';
import { systemAnnouncementCategoryColor } from '~/utils/system-announcement';

const props = withDefaults(
  defineProps<{
    category: SystemAnnouncementCategory;
    status?: SystemAnnouncementStatus;
    title: string;
    body: string;
    createdAt?: string | Date | null;
    isNew?: boolean;
    preview?: boolean;
    collapsible?: boolean;
    expanded?: boolean;
  }>(),
  {
    preview: false,
    collapsible: false,
    expanded: true,
  },
);

const { t } = useI18n();

const displayTitle = computed(() => props.title || (props.preview ? t('announcements.preview_title_placeholder') : ''));

const displayBody = computed(() => props.body || (props.preview ? t('announcements.preview_body_placeholder') : ''));

const formattedDate = computed(() => {
  const value = props.createdAt ?? (props.preview ? new Date() : null);
  if (!value) return '';
  return formatDateTime(value, undefined, 'short');
});
</script>

<template>
  <div class="system-announcement-display">
    <header class="d-flex align-center ga-2 mb-2">
      <v-chip
        size="small"
        :color="systemAnnouncementCategoryColor(category)"
        variant="tonal"
        label
      >
        {{ t(`enums.system_announcement_category.${category}`) }}
      </v-chip>
      <v-chip
        v-if="isNew"
        size="x-small"
        color="primary"
        variant="flat"
      >
        {{ t('announcements.new') }}
      </v-chip>
      <v-chip
        v-if="status === SystemAnnouncementStatus.DRAFT"
        size="x-small"
        color="secondary"
        variant="tonal"
      >
        {{ t('announcements.draft') }}
      </v-chip>
      <span
        v-if="formattedDate"
        class="announcement-meta ml-2 text-caption"
      >
        {{ formattedDate }}
      </span>
      <template v-if="$slots.actions">
        <v-spacer />
        <slot name="actions" />
      </template>
    </header>

    <h6 class="text-h6 mb-2">
      {{ displayTitle }}
    </h6>

    <div
      class="announcement-body"
      :class="{ 'is-collapsed': collapsible && !expanded }"
    >
      <MdPreview
        :model-value="displayBody"
        :sanitize="sanitizeMarkdownHtml"
        :theme="'light'"
        :preview-theme="'default'"
        no-katex
        no-mermaid
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.announcement-meta {
  color: rgb(var(--v-theme-muted-text));
}

.announcement-body {
  :deep(.md-editor-preview-wrapper) {
    padding: 0;
  }

  &.is-collapsed {
    overflow: hidden;
    max-height: 200px;
    transition: max-height 0.2s ease;
    mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
  }
}
</style>
